export enum PurchaseProcessStatus {
    DRAFT = 'DRAFT',
    ACTIVE = 'ACTIVE',
    LOST = 'LOST',
    CANCELED = 'CANCELED',
    FINISHED = 'FINISHED',
}

export enum PurchaseProcessStage {
    NEW = 'new',
    QUOTING = 'quoting',
    WAITING_APPROVAL = 'waiting_approval',
    APPROVED = 'approved',
    PURCHASE = 'purchase',
    PRODUCTION = 'production',
    INSPECTION = 'inspection',
    FINANCE = 'finance',
    FINISHED = 'finished',
}

export enum NeedSource {
    BRINDE_ME_ITEM = 'BRINDE_ME_ITEM', // legacy single-item need
    BRINDE_ME_ITEMS = 'BRINDE_ME_ITEMS', // Fase 2.2: 1..N budget items
    MANUAL = 'MANUAL',
}

export type SourceItemMatchStatus = 'MATCHED' | 'MISSING'

export interface NeedSourceItem {
    sourceItemExternalId: string
    productId?: string | null
    sku?: string | null
    description?: string | null
    quantity: number
    unitPrice?: number | null
    matchStatus?: SourceItemMatchStatus
}

export enum ExternalReferenceSystem {
    BRINDE_ME = 'BRINDE_ME',
    TIFLUX = 'TIFLUX',
    PIPEDRIVE = 'PIPEDRIVE',
}

export enum ExternalReferenceEntityType {
    BUDGET = 'BUDGET',
    ORDER = 'ORDER',
    TICKET = 'TICKET',
    DEAL = 'DEAL',
    PRODUCT = 'PRODUCT',
}

export enum SupplierQuoteStatus {
    REQUESTED = 'REQUESTED',
    RECEIVED = 'RECEIVED',
    REJECTED = 'REJECTED',
    CANCELED = 'CANCELED',
}

export interface AuditActor {
    mid: string
    /** uid do Firebase (registros novos); compara com o usuário logado. */
    uid?: string
    name: string
    mail: string
}

export interface Need {
    source: NeedSource
    sourceItemExternalId?: string
    productId?: string
    sku?: string
    description?: string
    quantity?: number
    notes?: string
    sourceItems?: NeedSourceItem[]
}

export interface ExternalReference {
    system: ExternalReferenceSystem
    entityType: ExternalReferenceEntityType
    externalId: string
    url?: string
    name?: string
}

export interface PurchaseProcess {
    _id: string
    code: string
    title: string
    status: PurchaseProcessStatus
    stageId: string
    stageName?: string
    selectedSupplierQuoteId?: string | null
    purchaseId?: string | null
    createdBy: AuditActor
    assignedTo?: AuditActor
    need: Need
    notes?: string
    externalReferences: ExternalReference[]
    sourceSnapshot?: Record<string, any> | null
    createdAt: Date | string
    updatedAt: Date | string
}

export interface PurchasingProductionLink {
    _id: string
    ticketExternalId: string
    orderExternalId?: string
    sourceItemExternalIds: string[]
    matchStatus: 'MATCHED' | 'NEEDS_REVIEW'
    matchSource: 'AUTOMATIC' | 'MANUAL'
    ticketSnapshot?: { title?: string; statusLabel?: string; updatedAt?: string }
    lastSyncedAt?: string | Date | null
}

export interface ProductionLinksResult {
    items: PurchasingProductionLink[]
    summary: { total: number; needsReview: number; lastSyncedAt: string | Date | null }
}

export interface SupplierSnapshot {
    supplierId?: string
    corporateName?: string
    tradeName?: string
    cnpj?: string
    contactName?: string
    mail?: string
    phone?: string
}

export interface SupplierQuote {
    _id: string
    purchaseProcessId: string
    supplierId: string
    supplierSnapshot?: SupplierSnapshot | null
    status: SupplierQuoteStatus
    quantity: number
    unitPriceCents: number
    setupFeeCents: number
    shippingFeeCents: number
    taxCents: number
    taxPercent?: number | null
    otherCostsCents: number
    subtotalCents: number
    totalCents: number
    leadTimeDays?: number
    paymentTerms?: string
    validUntil?: string | Date | null
    notes?: string
    requestedAt?: string | Date | null
    receivedAt?: string | Date | null
    active?: boolean
    createdBy?: AuditActor
    updatedBy?: AuditActor
    createdAt: Date | string
    updatedAt: Date | string
}

export interface PurchasingSupplier {
    _id: string
    corporateName: string
    tradeName?: string
    cnpj?: string
    contactName?: string
    phone?: string
    mail?: string
    additionalEmails?: string[]
    paymentTerms?: string
    defaultLeadTimeDays?: number
    observation?: string
    active?: boolean
}

export interface AuditEvent {
    _id: string
    purchaseProcessId: string
    action: string
    actor: AuditActor
    before?: Record<string, unknown>
    after?: Record<string, unknown>
    createdAt: Date | string
}

export interface PurchasingStage {
    _id: string
    key: string
    name: string
    position: number
    active: boolean
}

export interface PurchasingBoardCard {
    _id: string
    code: string
    title: string
    status: PurchaseProcessStatus
    stageId?: string | null
    assignedTo?: AuditActor
    customer?: string
    company?: string
    quantity?: number
    selectedSupplierQuoteId?: string | null
    purchase?: { _id?: string; status?: string; totalCents?: number } | null
    productionCount: number
    production?: Array<{ matchStatus: string; ticketSnapshot?: { statusLabel?: string } }>
    financeStatus: 'NONE' | 'PENDING' | 'PARTIAL' | 'PAID' | 'OVERDUE'
    stageEnteredAt: string
}

export interface PurchasingBoard {
    stages: PurchasingStage[]
    counts: Array<{ stageId: string; count: number }>
    assignees: Array<{ _id: string; name: string }>
    cards: PurchasingBoardCard[]
    total: number
    page: number
    limit: number
}

export interface SourceContextCustomer {
    externalId?: string | null
    name?: string | null
    email?: string | null
    phone?: string | null
    cpf?: string | null
}

export interface SourceContextCompany {
    externalId?: string | null
    name?: string | null
    tradeName?: string | null
    cnpj?: string | null
}

export interface SourceContextTotals {
    subtotal?: number | null
    total?: number | null
    shippingFee?: number | null
}

export interface SourceContextBudget {
    externalId: string
    code?: number | null
    status?: string | null
    lifecycleStage?: string | null
    type?: string | null
    customer?: SourceContextCustomer | null
    company?: SourceContextCompany | null
    totals?: SourceContextTotals | null
    paymentTerm?: string | null
    paymentForm?: string | null
    pipedriveDealId?: string | null
}

export interface SourceContextOrder {
    externalId?: string | null
    code?: number | null
    status?: string | null
    lifecycleStage?: string | null
}

export interface SourceContextItem {
    externalId?: string | null
    productId?: string | null
    sku?: string | null
    name?: string | null
    quantity?: number
    unitPrice?: number
    tifluxTicketId?: string | null
}

export interface SourceContext {
    source: string
    syncedAt: string
    budget: SourceContextBudget
    order?: SourceContextOrder | null
    items: SourceContextItem[]
    tiflux: {
        orderTicketId?: string | null
    }
}

// ---------------------------------------------------------------------------
// Fase 2.2 — budget item grouping / batch creation
// ---------------------------------------------------------------------------

export type GroupingReason =
    | 'SAME_PRODUCT_ID'
    | 'SAME_SKU'
    | 'SAME_NORMALIZED_NAME'

export interface SuggestedGroupItem {
    sourceItemExternalId: string
    productId?: string | null
    sku?: string | null
    description?: string | null
    quantity: number
    unitPrice?: number | null
}

export interface SuggestedGroup {
    key: string
    reason: GroupingReason
    confidence: 'HIGH'
    title: string
    suggestedQuantity: number | null
    items: SuggestedGroupItem[]
}

export interface ExistingProcessMapping {
    sourceItemExternalId: string
    processId: string
    processCode: string
    status: PurchaseProcessStatus
}

export interface PurchasePreparation {
    sourceContext: SourceContext
    existingProcessMappings: ExistingProcessMapping[]
    suggestedGroups: SuggestedGroup[]
}

// ---------------------------------------------------------------------------
// Fase 3 — Purchase
// ---------------------------------------------------------------------------

export const PurchaseStatus = {
    DRAFT: 'DRAFT',
    ORDERED: 'ORDERED',
    PARTIALLY_RECEIVED: 'PARTIALLY_RECEIVED',
    RECEIVED: 'RECEIVED',
    CHECKED: 'CHECKED',
    CANCELED: 'CANCELED',
} as const
export type PurchaseStatus = (typeof PurchaseStatus)[keyof typeof PurchaseStatus]

export interface SupplierQuoteSnapshot {
    supplierQuoteId: string
    quantity: number
    unitPriceCents: number
    setupFeeCents: number
    shippingFeeCents: number
    taxCents: number
    otherCostsCents: number
    subtotalCents: number
    totalCents: number
    paymentTerms?: string
    leadTimeDays?: number
    validUntil?: string | Date | null
    notes?: string
}

export interface Purchase {
    _id: string
    purchaseProcessId: string
    supplierId: string
    supplierSnapshot?: {
        corporateName?: string
        tradeName?: string
        cnpj?: string
        contactName?: string
        mail?: string
        phone?: string
    } | null
    supplierQuoteId: string
    supplierQuoteSnapshot?: SupplierQuoteSnapshot | null
    description?: string
    quantity: number
    unitPriceCents: number
    setupFeeCents: number
    shippingFeeCents: number
    taxCents: number
    otherCostsCents: number
    subtotalCents: number
    totalCents: number
    paymentTerms?: string
    leadTimeDays?: number
    expectedDeliveryDate?: string | Date | null
    issuedAt?: string | Date | null
    orderedAt?: string | Date | null
    receivedAt?: string | Date | null
    checkedAt?: string | Date | null
    status: PurchaseStatus
    notes?: string
    createdBy?: AuditActor
    updatedBy?: AuditActor
}

// ---------------------------------------------------------------------------
// Fase 4 — Financeiro / Pagamentos
// ---------------------------------------------------------------------------

export const PaymentStatus = {
    PENDING: 'PENDING',
    PAID: 'PAID',
    CANCELED: 'CANCELED',
} as const
export type PaymentStatus = (typeof PaymentStatus)[keyof typeof PaymentStatus]

export const PaymentType = {
    ADVANCE: 'ADVANCE',
    INSTALLMENT: 'INSTALLMENT',
    FINAL: 'FINAL',
    OTHER: 'OTHER',
} as const
export type PaymentType = (typeof PaymentType)[keyof typeof PaymentType]

export const PaymentMethod = {
    PIX: 'PIX',
    BOLETO: 'BOLETO',
    BANK_TRANSFER: 'BANK_TRANSFER',
    CARD: 'CARD',
    CASH: 'CASH',
    OTHER: 'OTHER',
} as const
export type PaymentMethod = (typeof PaymentMethod)[keyof typeof PaymentMethod]

export type FinanceStatus =
    | 'NOT_PLANNED'
    | 'UNPAID'
    | 'PARTIALLY_PAID'
    | 'PAID'

/** OVERDUE nunca é persistido — derivado de PENDING + dueDate < agora (§7). */
export interface PurchasePayment {
    _id: string
    purchaseId: string
    purchaseProcessId: string
    type: PaymentType
    amountCents: number
    dueDate?: string | Date | null
    status: PaymentStatus
    paymentMethod?: PaymentMethod
    paidAt?: string | Date | null
    transactionReference?: string
    notes?: string
    overdue?: boolean
    createdAt?: string | Date
    updatedAt?: string | Date
}

export interface FinanceSummary {
    totalCents: number
    plannedCents: number
    paidCents: number
    remainingToPlanCents: number
    outstandingCents: number
    paymentsTotal: number
    paymentsPaid: number
    paymentsPending: number
    paymentsCanceled: number
    paymentsOverdue: number
    nextDueDate: string | null
    financeStatus: FinanceStatus
}

// ---------------------------------------------------------------------------
// Fase 5 — Anexos / Documentos
// ---------------------------------------------------------------------------

export const AttachmentEntityType = {
    PURCHASE_PROCESS: 'PURCHASE_PROCESS',
    SUPPLIER_QUOTE: 'SUPPLIER_QUOTE',
    PURCHASE: 'PURCHASE',
    PAYMENT: 'PAYMENT',
} as const
export type AttachmentEntityType = (typeof AttachmentEntityType)[keyof typeof AttachmentEntityType]

export const AttachmentCategory = {
    CLIENT_REFERENCE: 'CLIENT_REFERENCE',
    SUPPLIER_QUOTE: 'SUPPLIER_QUOTE',
    ARTWORK: 'ARTWORK',
    MOCKUP: 'MOCKUP',
    PURCHASE_ORDER: 'PURCHASE_ORDER',
    INVOICE: 'INVOICE',
    BOLETO: 'BOLETO',
    PAYMENT_PROOF: 'PAYMENT_PROOF',
    OTHER: 'OTHER',
} as const
export type AttachmentCategory = (typeof AttachmentCategory)[keyof typeof AttachmentCategory]

export const ATTACHMENT_CATEGORIES_BY_ENTITY: Record<
    AttachmentEntityType,
    AttachmentCategory[]
> = {
    PURCHASE_PROCESS: [
        AttachmentCategory.CLIENT_REFERENCE,
        AttachmentCategory.ARTWORK,
        AttachmentCategory.MOCKUP,
        AttachmentCategory.OTHER,
    ],
    SUPPLIER_QUOTE: [
        AttachmentCategory.SUPPLIER_QUOTE,
        AttachmentCategory.OTHER,
    ],
    PURCHASE: [
        AttachmentCategory.PURCHASE_ORDER,
        AttachmentCategory.INVOICE,
        AttachmentCategory.ARTWORK,
        AttachmentCategory.MOCKUP,
        AttachmentCategory.OTHER,
    ],
    PAYMENT: [
        AttachmentCategory.BOLETO,
        AttachmentCategory.PAYMENT_PROOF,
        AttachmentCategory.OTHER,
    ],
}

export type AttachmentStatus = 'UPLOADING' | 'AVAILABLE' | 'FAILED' | 'DELETED'

export interface PurchasingAttachment {
    _id: string
    purchaseProcessId: string
    entityType: AttachmentEntityType
    entityId: string
    category: AttachmentCategory
    fileName: string
    originalFileName: string
    mimeType: string
    sizeBytes: number
    storageProvider: string
    storagePath: string
    description?: string
    status: AttachmentStatus
    uploadedBy?: AuditActor
    deletedBy?: AuditActor
    deletedAt?: string | Date | null
    createdAt?: string | Date
    updatedAt?: string | Date
}

export interface AttachmentGroup<T = PurchasingAttachment> {
    entityId: string
    attachments: T[]
}

export interface QuoteAttachmentGroup extends AttachmentGroup {
    label?: string | null
}

export interface PaymentAttachmentGroup extends AttachmentGroup {
    type?: string
    amountCents?: number
    status?: string
}

export interface ProcessAttachmentsAggregate {
    process: PurchasingAttachment[]
    quotes: QuoteAttachmentGroup[]
    purchase: PurchasingAttachment[]
    payments: PaymentAttachmentGroup[]
}
